'use client';

/**
 * ProductHeroCard — hero image + thumbnail gallery slice.
 *
 * Slices the substrate-aware `primary_image` + `gallery_images` fields
 * on `FullProductDetail` from the shared `graph_product_explore`
 * payload. One of three cards in the mode4_product overlay preset;
 * shares ONE network round-trip with ProductSummaryStripCard +
 * ProductDescriptionCard via React Query dedup on
 * `(strategy, semantic_id)`.
 *
 * The substrate routes each image via a verdict ladder (cutout → trimmed
 * → source) at the BFF and labels which rung it landed on via
 * `treatment`. This card just paints what's shipped; the only
 * consumer-side decision is whether to re-ground a cutout subject with
 * a CSS drop-shadow, handled by `<CutoutShadow>`. See
 * klyde_edge/ui_config/docs/derived_image_concept.md — "Reference BFF
 * + frontend example" — for the substrate contract.
 *
 * Falls back to the legacy `images: ProductImageSet` field when the
 * substrate hasn't produced derivatives yet (worker mid-grind) or for
 * pre-substrate sources. Falls behind gracefully; never breaks.
 *
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="product_hero"
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas / overlay
 */

import { useEffect, useMemo, useState } from 'react';
import { ImageOff, Loader2, Sparkles, Image as ImageIcon } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { text } from '@/lib/design/tokens';
import { useProductLabel } from '@/lib/stores/total-matrix-store';
import { useCardExport } from '@/lib/design/card-export';
import { CutoutShadow } from '@/lib/design/cutout-shadow';
import { ImageZoomModal } from '@/lib/design/image-zoom-modal';
import type {
  ImagePayload,
  ImageTreatment,
  ProductExploreResponse,
} from '@/lib/api/types';
import { cn } from '@/lib/utils/cn';
import { CardShell } from '../card-shell';

const PRODUCT_EXPLORE_STRATEGY = 'graph_product_explore';

type DisplayImage = {
  hero: string;
  thumb: string;
  treatment: ImageTreatment;
  contactShadow: boolean;
  // Vendor's original URL, preserved alongside the substrate's chosen
  // hero/thumb so the per-card "View original" toggle can flip between
  // the two without re-querying the BFF. Null only when the BFF
  // payload itself didn't supply a source_fallback (shouldn't happen
  // post-substrate, but the type is honest about it).
  sourceUrl: string | null;
};

function resolveImageEntry(
  img: ImagePayload | null | undefined,
  theme: 'light' | 'dark',
): DisplayImage | null {
  if (!img) return null;
  const hero = img.hero?.[theme] ?? img.source_fallback ?? '';
  const thumb = img.thumb?.[theme] ?? img.source_fallback ?? '';
  if (!hero && !thumb) return null;
  return {
    hero,
    thumb,
    treatment: img.treatment ?? 'source',
    contactShadow: !!img.contact_shadow,
    sourceUrl: img.source_fallback ?? null,
  };
}

export function ProductHeroCard() {
  const { entityId, entityType } = useViewportSelection();
  const enabled = !!entityId && entityType === 'product';

  // i18n chrome labels — server-driven dict (Layer 1 per
  // klyde_edge/docs/i18n_concept.md). Every visible string comes
  // from here; hardcoded English in JSX would be a doctrine breach.
  const labelEnhanced = useProductLabel('hero_source_enhanced');
  const labelOriginal = useProductLabel('hero_source_original');
  const labelLoading = useProductLabel('hero_loading');
  const labelNoImages = useProductLabel('hero_no_images');
  const labelImageAlt = useProductLabel('hero_image_alt_fallback');

  const { data, isLoading } = useStrategyData<ProductExploreResponse>(
    PRODUCT_EXPLORE_STRATEGY,
    enabled ? { semantic_id: entityId } : {},
    enabled,
  );
  const product = data?.data?.product ?? null;
  const { resolvedTheme } = useTheme();
  const theme: 'light' | 'dark' = resolvedTheme === 'dark' ? 'dark' : 'light';

  // Scene export — overlay (PDP) surface. Gallery URLs are theme-resolved
  // and heavy; the scene carries the count + primary presence, not the
  // pixels (the payload half is the shared graph_product_explore entry).
  useCardExport(useMemo(() => {
    const substrateCount = product?.primary_image
      ? 1 + (product.gallery_images?.length ?? 0)
      : 0;
    const legacyCount = product?.images?.gallery_urls?.length ?? 0;
    const imageCount = substrateCount || legacyCount;
    return {
      label: 'Images',
      content_key: 'product_hero',
      surface: 'overlay' as const,
      status: (product ? 'loaded' : enabled ? 'loading' : 'idle') as 'loading' | 'loaded' | 'idle',
      query_key: enabled
        ? ['strategy-data', PRODUCT_EXPLORE_STRATEGY, { semantic_id: entityId }]
        : null,
      text: imageCount ? `${imageCount} image${imageCount === 1 ? '' : 's'}` : 'Images',
      json: product
        ? { name: product.name ?? null, image_count: imageCount, has_primary: !!product.primary_image }
        : null,
    };
  }, [product, enabled, entityId]));

  const entries: DisplayImage[] = useMemo(() => {
    if (!product) return [];

    // Substrate path (preferred). primary_image first, then gallery
    // in sequence. Each entry's hero/thumb URL is resolved via
    // [slot][theme] with `??` fall back to source_fallback so we
    // never land on null.
    if (product.primary_image) {
      const all = [product.primary_image, ...(product.gallery_images ?? [])];
      return all
        .map((img) => resolveImageEntry(img, theme))
        .filter((e): e is DisplayImage => e !== null);
    }

    // Legacy fallback path — pre-substrate Product or worker mid-grind.
    // Mirror the prior gallery-first convention so surfaces that haven't
    // been observed against the substrate yet don't drift in behaviour.
    // Legacy URLs route to 'source' treatment with no shadow signal — the
    // substrate hasn't classified them, so we conservatively assume the
    // source has its own photographed shadow (no CSS shadow added).
    const legacy = product.images;
    const galleryUrls = legacy?.gallery_urls ?? [];
    if (galleryUrls.length > 0) {
      return galleryUrls.map((url) => ({
        hero: url,
        thumb: url,
        treatment: 'source' as const,
        contactShadow: false,
        sourceUrl: url,
      }));
    }
    const heroPrimary =
      legacy?.large ?? legacy?.medium ?? legacy?.primary ?? null;
    if (heroPrimary) {
      return [{
        hero: heroPrimary,
        thumb: heroPrimary,
        treatment: 'source' as const,
        contactShadow: false,
        sourceUrl: heroPrimary,
      }];
    }
    return [];
  }, [product, theme]);

  const [activeIdx, setActiveIdx] = useState(0);
  // Per-card "view original vendor image" toggle. Local state, not a
  // shared store — the affordance is scoped to this concertina overlay
  // surface and resets with the product. Rationale + future expansion
  // path documented in klyde_edge/ui_config/docs/derived_image_concept.md
  // (the per-consumer-surface choice doctrine).
  const [showOriginal, setShowOriginal] = useState(false);
  // Zoom modal open/closed. Independent of the toggle above: zoom
  // always uses the source URL (highest resolution + vendor JPEG
  // quality), regardless of which file the rendered hero is on. The
  // substrate's derivatives are tuned for "looks good at native
  // size," not "holds up under 3× zoom."
  const [zoomOpen, setZoomOpen] = useState(false);
  // Reset focus + toggle + zoom when the product changes — without
  // this state persists across navigation and surfaces stale state on
  // the next product.
  useEffect(() => {
    setActiveIdx(0);
    setShowOriginal(false);
    setZoomOpen(false);
  }, [product?.semantic_id]);

  if (isLoading && !product) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        {labelLoading}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-2 text-muted-foreground">
        <ImageOff className="h-8 w-8" />
        <div className={text.eyebrow}>{labelNoImages}</div>
      </div>
    );
  }

  const focused = entries[Math.min(activeIdx, entries.length - 1)];

  // When the toggle is on AND a source URL exists, both hero and thumb
  // route to it; treatment becomes 'source' so CutoutShadow goes inert.
  // When off (default), the BFF's chosen URL paints. The substrate
  // payload always carries source_fallback so the swap is local — no
  // re-query, no flicker.
  const heroUrl =
    showOriginal && focused.sourceUrl ? focused.sourceUrl : focused.hero;
  const heroTreatment: ImageTreatment =
    showOriginal && focused.sourceUrl ? 'source' : focused.treatment;
  const canToggle = focused.sourceUrl !== null;

  return (
    <CardShell className="h-full flex flex-col gap-3 overflow-hidden p-2">
      {/* Image plate — constrained max-width so it doesn't sprawl on
          wide overlays. Without the cap, full-bleed cells leave the
          plate ~1300px wide hosting a ~700px image, so the bg-muted
          shows as empty grey panels on either side of the photo (reads
          as three rectangles, not one product shot). max-w-4xl keeps
          the plate sized to the photo; the cell's transparent
          background shows around it. Fixed plate height so the contained
          photo sits at a stable size framed by the plate, independent of
          the band / stack height (was flex-fill, which let the stack's
          min-height band grow the photo edge-to-edge). `relative` so the
          toggle button can sit absolutely in the top-right corner. */}
      <div className="relative h-[280px] shrink-0 mx-auto w-full max-w-4xl flex items-center justify-center bg-muted/50 rounded-md overflow-hidden">
        {canToggle && (
          // Per-card affordance: segmented control showing both states,
          // active one highlighted. State-not-action labeling — the
          // user reads "which one am I on?" not "what does this button
          // do?" — removes the click-action ambiguity of a single
          // toggle. Lives in the hero's corner so it's discoverable
          // without occupying layout space. The substrate produces
          // good results on ~90% of catalogue; this is the escape
          // hatch for the rest.
          <div
            role="radiogroup"
            aria-label="Image source"
            className={cn(
              'absolute top-2 right-2 z-10 flex items-center',
              'rounded-md border border-border overflow-hidden',
              'bg-background/80 backdrop-blur-sm',
            )}
          >
            {/* Icon-only segmented toggle — labels move to tooltips (title +
                aria-label, both from the BFF label lane) so the two-state
                "which am I on?" clarity holds without the width of two text
                pills. Active rung highlighted primary. */}
            <button
              type="button"
              role="radio"
              aria-checked={!showOriginal}
              aria-label={labelEnhanced}
              title={labelEnhanced}
              onClick={() => setShowOriginal(false)}
              className={cn(
                'flex items-center p-1.5 transition-colors',
                !showOriginal
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
              )}
            >
              <Sparkles className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={showOriginal}
              aria-label={labelOriginal}
              title={labelOriginal}
              onClick={() => setShowOriginal(true)}
              className={cn(
                'flex items-center p-1.5 transition-colors',
                'border-l border-border',
                showOriginal
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
              )}
            >
              <ImageIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        {/* CutoutShadow re-grounds the subject when treatment==='cutout' and
            the source had a photographed contact shadow that the alpha mask
            stripped. Inert when treatment is 'source' (toggle on) — the
            vendor's original already has its photographed shadow. */}
        <CutoutShadow
          treatment={heroTreatment}
          contactShadow={focused.contactShadow}
          // h-full + w-full (not max-) so the wrapper takes the plate's
          // explicit size and the <img>'s `max-h-full max-w-full
          // object-contain` resolves against that. With `max-h-full` on
          // the wrapper, the wrapper had no actual height — img sized to
          // its natural height (e.g. 739px), wrapper grew to match, and
          // the plate's `overflow-hidden` cropped the bottom of any
          // image taller than the plate. Bug surfaced on Access Chair
          // (cutout 472×739 in a shorter plate).
          className="h-full w-full flex items-center justify-center"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={heroUrl}
            alt={product?.name ?? labelImageAlt}
            className={cn(
              'max-h-full max-w-full object-contain',
              // Cursor + click-to-zoom only enabled when a source URL
              // exists to zoom into. Without it the modal would render
              // the same (possibly upscale-blurry) derivative.
              focused.sourceUrl && 'cursor-zoom-in',
            )}
            onClick={
              focused.sourceUrl ? () => setZoomOpen(true) : undefined
            }
          />
        </CutoutShadow>
      </div>
      {/* Zoom modal — always targets source URL for inspection
          fidelity, regardless of which file is rendered on the
          hero. Open state is local; ESC / backdrop / close button
          dismisses. */}
      {focused.sourceUrl && (
        <ImageZoomModal
          src={focused.sourceUrl}
          alt={product?.name ?? 'Product image'}
          open={zoomOpen}
          onClose={() => setZoomOpen(false)}
        />
      )}
      {entries.length > 1 && (
        <div className="shrink-0 flex gap-2 overflow-x-auto pb-1">
          {entries.map((entry, i) => {
            const isActive = i === activeIdx;
            // Same swap on the thumb strip — consistent surface when
            // the user has chosen to see originals.
            const thumbUrl =
              showOriginal && entry.sourceUrl ? entry.sourceUrl : entry.thumb;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setActiveIdx(i)}
                className={cn(
                  'shrink-0 h-16 w-16 rounded border bg-muted/30 overflow-hidden transition-colors',
                  isActive
                    ? 'border-primary ring-1 ring-primary/40'
                    : 'border-border hover:border-muted-foreground',
                )}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={thumbUrl}
                  alt=""
                  className="h-full w-full object-contain"
                />
              </button>
            );
          })}
        </div>
      )}
    </CardShell>
  );
}
